import React, { useState, useEffect, useRef } from "react";

// Modules

import EditorJS from "@editorjs/editorjs";

import Title from "../../editorjs/blocks/title/title";
import Subtitle from "../../editorjs/blocks/subtitle/subtitle";
import Body from "../../editorjs/blocks/body";
import Summary from "../../editorjs/blocks/summary";
import NestedList from "../../editorjs/blocks/nested-list";
import CaptionedImage from "../../editorjs/blocks/captioned-image";
import Gallery from "../../editorjs/blocks/gallery";
import Table from "../../editorjs/blocks/table";
import Video from "../../editorjs/blocks/video";
import Embed from "../../editorjs/blocks/embed";
import HorizontalRule from "../../editorjs/blocks/horizontal-rule";
import ButtonGroup from "../../editorjs/blocks/button-group";
import Chart from "../../editorjs/blocks/chart";
import Map from "../../editorjs/blocks/map";
import Question from "../../editorjs/blocks/question";
import TrainingSection from "../../editorjs/blocks/training-section";
import ContactGroup from "../../editorjs/blocks/contact-group";

import FontSizeTune from "../../editorjs/blockTunes/font-size";
import Hyperlink from "../../editorjs/inlineTunes/hyperlink-tool";
import SubscriptTool from "../../editorjs/inlineTunes/subscript-tool";
import SuperscriptTool from "../../editorjs/inlineTunes/superscript-tool";
import OverlayTool from "../../editorjs/blockTunes/overlay-tool";
import MarginTool from "../../editorjs/blockTunes/margin";
import DimensionTune from "../../editorjs/blockTunes/dimension";

import MediaLibrary from "../mediaLibrary";
import EntitySelector from "../../screens/training/entitySelector";
import { safeUpdateBlock } from "../../utils/helpers";
import { assetBase } from "../../config";
import { getTuneColors } from "../../core/slideHandlers";
import { getThemeJson } from "../../utils/helpers";

import PostMuiThemePreload from "./postMuiThemePreload";

export default function PostBodyEdit(props) {
  const [currentBlock, setCurrentBlock] = useState(null);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [showSelector, setShowSelector] = useState(false);

  const theme_json = getThemeJson("companyapp_2025");
  const themeDirectory = `${assetBase}/themes/${"companyapp"}`;

  const editorInstance = useRef(null);
  useEffect(() => {
    if (!editorInstance.current) {
      initEditor();
    }
    // eslint-disable-next-line
  }, []);

  const initEditor = () => {
    const {
      title,
      subtitle,
      summary,
      body,
      list,
      table,
      image,
      gallery,
      video,
      embed,
      rule,
      buttonGroup,
      chart,
      map,
      question,
      trainingSection,
      contactGroup,
    } = props.editorProps || {};

    const _title = title
      ? {
          title: {
            class: Title,
            toolbox: {
              title: "Heading",
            },
            inlineToolbar: [
              "bold",
              "italic",
              "Subscript",
              "Superscript",
              "Hyperlink",
            ],
            config: {
              entity: true,
              muiClassName: "css-13h0xye-MuiTypography-root",
            },
            tunes: ["FontSizeTune", "MarginTool"],
          },
        }
      : {};

    const _subtitle = subtitle
      ? {
          subtitle: {
            class: Subtitle,
            toolbox: {
              title: "Sub-heading",
            },
            inlineToolbar: [
              "bold",
              "italic",
              "Subscript",
              "Superscript",
              "Hyperlink",
            ],
            config: {
              entity: true,
              muiClassName: "css-3ehian-MuiTypography-root",
            },
            tunes: ["FontSizeTune", "MarginTool"],
          },
        }
      : {};

    const _summary = summary
      ? {
          summary: {
            class: Summary,
          },
        }
      : {};

    const _body = body
      ? {
          body: {
            class: Body,
            config: {
              enableSummary: true,
              entity: true,
              muiClassName: "css-1wvnryc-MuiTypography-root",
            },
            inlineToolbar: [
              "bold",
              "italic",
              "Subscript",
              "Superscript",
              "Hyperlink",
            ],
            tunes: ["FontSizeTune", "MarginTool"],
          },
        }
      : {};

    const _list = list
      ? {
          list: {
            class: NestedList,
            inlineToolbar: [
              "bold",
              "italic",
              "Subscript",
              "Superscript",
              "Hyperlink",
            ],
            config: {
              entity: true,
              muiClassName: "css-1wvnryc-MuiTypography-root",
            },
            tunes: ["FontSizeTune", "MarginTool"],
          },
        }
      : {};

    const _table = table
      ? {
          table: {
            class: Table,
            // tunes: ["Animation", "AlignmentBlockTune", "FontColorTool"],
            inlineToolbar: [
              "bold",
              "italic",
              "Subscript",
              "Superscript",
              "Hyperlink",
            ],
            config: {
              entity: true,
              stylesEnabled: true,
              muiClassName: "css-1nxf9qn-MuiTypography-root",
              options: {
                swatches:
                  getTuneColors(theme_json)?.length > 0
                    ? ["#00000000", ...getTuneColors(theme_json)]
                    : ["#fff", "#e9e9e9", "#777", "#000"],
              },
            },
            tunes: ["DimensionTune", "MarginTool"],
          },
        }
      : {};

    const _image = image
      ? {
          image: {
            class: CaptionedImage,
            config: {
              showMediaLibrary: (block) => {
                setCurrentBlock(block);
                setShowMediaLibrary(true);
              },
              entity: true,
              muiClassName: "css-1wvnryc-MuiTypography-root",
            },
            tunes: ["OverlayTool", "DimensionTune", "MarginTool"],
          },
        }
      : {};

    const _gallery = gallery
      ? {
          gallery: {
            class: Gallery,
            config: {
              showMediaLibrary: (block) => {
                setCurrentBlock(block);
                setShowMediaLibrary(true);
              },
            },
          },
        }
      : {};

    const _video = video
      ? {
          video: {
            class: Video,
            config: {
              showMediaLibrary: (block) => {
                setCurrentBlock(block);
                setShowMediaLibrary(true);
              },
            },
            tunes: ["DimensionTune", "MarginTool"],
          },
        }
      : {};

    const _embed = embed
      ? {
          embed: {
            class: Embed,
            config: {
              showMediaLibrary: (block) => {
                setCurrentBlock(block);
                setShowMediaLibrary(true);
              },
              disableFallback: true,
            },
            tunes: ["DimensionTune", "MarginTool"],
          },
        }
      : {};

    const _rule = rule
      ? {
          rule: {
            class: HorizontalRule,
            tunes: ["DimensionTune", "MarginTool"],
          },
        }
      : {};

    const _buttonGroup = buttonGroup
      ? {
          buttonGroup: {
            class: ButtonGroup,
            tunes: ["DimensionTune", "MarginTool"],
          },
        }
      : {};

    const _chart = chart
      ? {
          chart: {
            class: Chart,
            tunes: ["DimensionTune", "MarginTool"],
            config: {
              options: {
                swatches:
                  getTuneColors(theme_json)?.length > 0
                    ? ["#00000000", ...getTuneColors(theme_json)]
                    : ["#fff", "#e9e9e9", "#777", "#000"],
              },
              leftStart: 508,
            },
          },
        }
      : {};

    const _map = map
      ? {
          map: {
            class: Map,
            tunes: ["DimensionTune", "MarginTool"],
            config: {
              showMediaLibrary: (block) => {
                setCurrentBlock(block);
                setShowMediaLibrary(true);
              },
              googleApiKey: theme_json?.googleApiKey,
              themeDirectory: themeDirectory,
              pin: theme_json?.icons?.pin,
              disableFallback: true,
            },
          },
        }
      : {};

    const _question = question
      ? {
          question: {
            class: Question,
            tunes: ["FontSizeTune", "MarginTool"],
            inlineToolbar: [
              "bold",
              "italic",
              "Subscript",
              "Superscript",
              "Hyperlink",
            ],
          },
        }
      : {};

    const _trainingSection = trainingSection
      ? {
          trainingSection: {
            class: TrainingSection,
            tunes: ["FontSizeTune", "MarginTool"],
            inlineToolbar: [
              "bold",
              "italic",
              "Subscript",
              "Superscript",
              "Hyperlink",
            ],
            config: {
              showEntitySelector: (block) => {
                setCurrentBlock(block);
                setShowSelector(true);
              },
            },
          },
        }
      : {};

    const _contactGroup = contactGroup
      ? {
          contactGroup: {
            class: ContactGroup,
            tunes: ["MarginTool"],
          },
        }
      : {};

    const tools = {
      ..._title,
      ..._subtitle,
      ..._summary,
      ..._body,
      ..._list,
      ..._table,
      ..._image,
      ..._gallery,
      ..._video,
      ..._embed,
      ..._rule,
      ..._buttonGroup,
      ..._chart,
      ..._map,
      ..._question,
      ..._trainingSection,
      ..._contactGroup,
      // Tune settings
      FontSizeTune: {
        class: FontSizeTune,
        config: {
          enabledBlocks: ["title", "subtitle", "body", "list"],
        },
      },
      OverlayTool: {
        class: OverlayTool,
        config: {
          options:
            getTuneColors(theme_json)?.length > 0
              ? getTuneColors(theme_json)
              : ["#fff", "#e9e9e9", "#777", "#000"],
          // default: slideLayout?.default_font_color
          //   ? slideLayout?.default_font_color
          //   : "",

          blocks: {
            // body: '',
          },
        },
      },
      MarginTool: {
        class: MarginTool,
      },
      DimensionTune: {
        class: DimensionTune,
        config: {
          width: {
            unitToggle: true,
            min: 0,
          },
          height: {
            min: 20,
          },
        },
      },

      // Inline tools
      Subscript: {
        class: SubscriptTool,
      },
      Superscript: {
        class: SuperscriptTool,
      },
      Hyperlink: {
        class: Hyperlink,
        config: {
          options: [
            "#ffffff",
            "#52b3f4",
            "#1d9bf1",
            "#20bf55",
            "#bf1e2c",
            "#212931",
            "#f9690e",
            "#999999",
            "#16324f",
            "#415262",
          ],
        },
      },
    };

    const setBlockAttributes = (editor) => {
      const blockLength = editor.blocks.getBlocksCount();
      let contentIndex = 0;
      let imageIndex = 0;

      for (var i = 0; i < blockLength; i++) {
        const block = editor.blocks.getBlockByIndex(i);
        if (block && block.holder && block.name) {
          const element = block?.holder;
          const blockType = block?.name;

          element?.classList.add(blockType);

          if (blockType === "image" || blockType === "inlineImage") {
            imageIndex++;
            element?.setAttribute("data-block", imageIndex);
          }

          if (blockType === "body" || blockType === "list") {
            contentIndex++;
            element?.setAttribute("data-block", contentIndex);
          }
        }
      }
    };

    const editor = new EditorJS({
      holder: "post-body-edit-holder",
      data: props.editorContent,
      tools: {
        ...tools,
      },
      defaultBlock: "body",
      onReady: () => {
        editorInstance.current = editor;
        setBlockAttributes(editorInstance.current);
      },
      onChange: async (api) => {
        setBlockAttributes(editorInstance.current);
        let content = await editor.save();
        props.setEditorContent(content);
      },
    });
  };
  return (
    <div id="post-body-edit-holder" className="post-body-edit">
      <PostMuiThemePreload />
      {showMediaLibrary && (
        <MediaLibrary
          user={props.user}
          csrf_token={props.csrf_token}
          mediaType={currentBlock?.bundle ? currentBlock.bundle : "all"}
          setShowMediaLibrary={(condition) => {
            setShowMediaLibrary(condition);
          }}
          files={props.editorProps?.files ? props.editorProps.files : {}}
          showMediaLibrary={showMediaLibrary}
          onMediaInsert={(media) => {
            if (currentBlock?.details) {
              let data = currentBlock?.data;
              let newData = { ...data };

              if (currentBlock?.bundle === "image") {
                if (currentBlock?.details.name === "gallery") {
                  const imageData = {
                    mid: media.mid,
                    url: media.field_media_image,
                    caption: media.alt,
                    x: media.x,
                    y: media.y,
                    width: media.width,
                    height: media.height,
                    maxWidth: media.maxWidth,
                    maxHeight: media.maxHeight,
                    listingId: media?.listingId,
                    listingName: media?.listingName,
                    assetId: media?.assetId,
                    fileId: media?.fileId,
                    iceMedia: media?.iceMedia,
                  };

                  if (Array.isArray(data.images)) {
                    newData.images.push(imageData);
                  } else {
                    newData.images = [];
                    newData.images.push(imageData);
                  }
                } else if (currentBlock?.details.name === "imageTextOverlay") {
                  newData.images[currentBlock?.imageIndex].mid = media.mid;
                  newData.images[currentBlock?.imageIndex].url =
                    media.field_media_image;
                  newData.images[currentBlock?.imageIndex].x = media.x;
                  newData.images[currentBlock?.imageIndex].y = media.y;
                  newData.images[currentBlock?.imageIndex].width = media.width;
                  newData.images[currentBlock?.imageIndex].height =
                    media.height;
                  newData.images[currentBlock?.imageIndex].maxWidth =
                    media.maxWidth;
                  newData.images[currentBlock?.imageIndex].maxHeight =
                    media.maxHeight;
                  newData.images[currentBlock?.imageIndex].listingId =
                    media.listingId;
                  newData.images[currentBlock?.imageIndex].listingName =
                    media.listingName;
                  newData.images[currentBlock?.imageIndex].assetId =
                    media.assetId;
                  newData.images[currentBlock?.imageIndex].fileId =
                    media.fileId;
                  newData.images[currentBlock?.imageIndex].iceMedia =
                    media.iceMedia;
                } else {
                  newData.mid = media.mid;
                  newData.url = media.field_media_image;
                  newData.x = media.x;
                  newData.y = media.y;
                  newData.width = media.width;
                  newData.height = media.height;
                  newData.maxWidth = media.maxWidth;
                  newData.maxHeight = media.maxHeight;
                  newData.listingId = media.listingId;
                  newData.listingName = media.listingName;
                  newData.assetId = media.assetId;
                  newData.fileId = media.fileId;
                  newData.iceMedia = media.iceMedia;
                }
              }

              if (currentBlock?.bundle === "video") {
                newData.mid = media.mid;
                newData.url = media.field_media_video;
                newData.timestamp = media.timestamp;
                newData.poster = `${assetBase}/videos/${media.mid}.jpg`;
                newData.listingId = media.listingId;
                newData.listingName = media.listingName;
                newData.assetId = media.assetId;
                newData.fileId = media.fileId;
                newData.iceMedia = media.iceMedia;

                if (media.external) {
                  newData.external = true;
                  newData.url = media.url;
                  newData.poster = media.poster;
                }
              }
              safeUpdateBlock(
                editorInstance.current,
                currentBlock?.details?.id,
                newData,
              );
            }
          }}
          selectedMid={currentBlock?.data?.mid}
          selectedListingId={currentBlock?.data?.listingId}
          selectedAssetId={currentBlock?.data?.assetId}
          selectedFileId={currentBlock?.data?.fileId}
          selectedIceMedia={currentBlock?.data?.iceMedia}
          currentBlock={currentBlock}
        />
      )}

      {showSelector && (
        <EntitySelector
          currentBlock={currentBlock}
          // Training sections only reference this set (mirrors the backend
          // EntitySearchResource default + Training::referenceFieldSettings).
          allowedTypes={[
            "presentation",
            "document",
            "video",
            "gallery",
            "podcast",
            "quiz",
            "webform",
          ]}
          showSelector={showSelector}
          setShowSelector={(value) => {
            setShowSelector(value);
            setCurrentBlock(null);
          }}
          insertSelections={(selections) => {
            let data = currentBlock?.data;
            let newData = { ...data };
            newData.entities = [...selections];
            safeUpdateBlock(
              editorInstance.current,
              currentBlock?.details?.id,
              newData,
            );
            setCurrentBlock(null);
          }}
        />
      )}
    </div>
  );
}
